import { Link, useParams } from "react-router-dom";
import { AppLayout } from "@/components/Layout";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/Card";
import { Alert } from "@/components/Alert";
import { HelpRenderer } from "@/components/HelpRenderer";
import {
  HELP_CATEGORIES,
  getArticleById,
  getArticlesByCategory,
} from "@/data/helpArticles";

/**
 * Individual help article. Shows:
 *   - Breadcrumb: Help → {Category}
 *   - Title + body
 *   - "Related articles" sidebar (other articles in the same category)
 *   - Footer CTAs: was this helpful + message care team
 *
 * If the slug doesn't exist, render an Alert + "back to Help Center" link
 * rather than the 404 page — patient context says "they're looking for help,"
 * not "broken URL."
 */
export default function HelpArticle() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;

  if (!article) {
    return (
      <AppLayout>
        <PageHeader title="Article not found" />
        <Alert tone="error">
          We couldn't find that help article. It may have moved or been renamed.{" "}
          <Link to="/help" className="underline font-semibold">
            Browse all help articles →
          </Link>
        </Alert>
      </AppLayout>
    );
  }

  const category = HELP_CATEGORIES.find((c) => c.id === article.category);
  const related = getArticlesByCategory(article.category).filter((a) => a.id !== article.id).slice(0, 5);

  return (
    <AppLayout>
      {/* Breadcrumb. Cheap navigation that doubles as orientation — patients
          who landed here from search benefit from seeing where this article
          lives in the taxonomy. */}
      <nav className="mb-3 text-[12px] text-fh-gray flex items-center gap-1.5 flex-wrap" aria-label="Breadcrumb">
        <Link to="/help" className="hover:text-fh-ink underline-offset-2 hover:underline">
          Help Center
        </Link>
        <span>›</span>
        <span className="text-fh-ink font-semibold">{category?.label ?? "Article"}</span>
      </nav>

      <PageHeader title={article.title} subtitle={article.summary} />

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6">
        {/* Article body */}
        <article className="bg-white border border-fh-border rounded-tile p-6 md:p-8">
          <HelpRenderer blocks={article.body} />

          {/* Footer CTAs — feedback + escalation. Feedback is fire-and-forget
              right now (console-only); when we wire a help_feedback table we
              just replace the handler. */}
          <div className="mt-8 pt-6 border-t border-fh-border">
            <p className="text-[13px] text-fh-gray m-0 mb-2 font-semibold">
              Was this helpful?
            </p>
            <FeedbackButtons articleId={article.id} />
          </div>

          <div className="mt-6 rounded-[10px] bg-fh-sage/30 border border-fh-green-light/40 p-4">
            <p className="text-[13.5px] text-fh-ink m-0 mb-2">
              Need more help? Send a message to your care team — replies within 1 business day.
            </p>
            <Link
              to="/messages"
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-[8px] bg-fh-green-dark text-white text-[13px] font-semibold hover:bg-fh-green transition"
            >
              Open Messages →
            </Link>
          </div>
        </article>

        {/* Related articles. Visible on lg+; collapses below the article on
            narrower screens. */}
        <aside className="space-y-4">
          {related.length > 0 && (
            <Card title={`More in ${category?.label ?? "this category"}`}>
              <ul className="space-y-2">
                {related.map((a) => (
                  <li key={a.id}>
                    <Link
                      to={`/help/${a.id}`}
                      className="text-[13.5px] text-fh-green-dark hover:text-fh-green hover:underline underline-offset-2 leading-snug block"
                    >
                      {a.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </Card>
          )}
          <Card>
            <p className="text-[12.5px] text-fh-gray m-0 mb-2 font-semibold uppercase tracking-wider">
              Quick links
            </p>
            <ul className="space-y-1.5 text-[13.5px]">
              <li><Link to="/messages" className="text-fh-green-dark hover:underline">Message care team</Link></li>
              <li><Link to="/billing" className="text-fh-green-dark hover:underline">Billing &amp; invoices</Link></li>
              <li><Link to="/account" className="text-fh-green-dark hover:underline">Account settings</Link></li>
              <li><Link to="/account/privacy" className="text-fh-green-dark hover:underline">Privacy &amp; data</Link></li>
              <li><Link to="/labs" className="text-fh-green-dark hover:underline">Lab results</Link></li>
            </ul>
          </Card>
        </aside>
      </div>
    </AppLayout>
  );
}

function FeedbackButtons({ articleId }: { articleId: string }) {
  // Local-only state for now. When we add a help_feedback table, swap the
  // console.log for an edge-function call. Storing only article id + vote
  // (no patient PII) keeps this an analytics signal, not PHI.
  function vote(positive: boolean) {
    // eslint-disable-next-line no-console
    console.info(`help_feedback:${articleId}:${positive ? "yes" : "no"}`);
  }
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => vote(true)}
        className="px-3 py-1.5 rounded-[8px] border border-fh-border text-[13px] font-semibold text-fh-ink hover:border-fh-green hover:text-fh-green-dark transition"
      >
        👍 Yes
      </button>
      <button
        type="button"
        onClick={() => vote(false)}
        className="px-3 py-1.5 rounded-[8px] border border-fh-border text-[13px] font-semibold text-fh-ink hover:border-fh-green hover:text-fh-green-dark transition"
      >
        👎 No
      </button>
      <span className="text-[11.5px] text-fh-gray-light ml-1">
        We use this to improve articles.
      </span>
    </div>
  );
}
